import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRutas } from "../../hooks/useRutas";
import axios from "axios";
import { useAuth } from "../../context/authContext";

// Devuelve 'black' o 'white' según contraste legible sobre un color de fondo hex
const getContrastColor = (hex) => {
  if (!hex) return '#000';
  const c = hex.replace('#','');
  const r = parseInt(c.substring(0,2),16);
  const g = parseInt(c.substring(2,4),16);
  const b = parseInt(c.substring(4,6),16);
  // luminancia relativa
  const lum = 0.2126*r + 0.7152*g + 0.0722*b;
  return lum > 150 ? '#000' : '#fff';
};

// Darken hex color by percent (0-100)
const darkenColor = (hex, percent) => {
  if (!hex) return hex;
  const c = hex.replace('#','');
  const num = parseInt(c,16);
  let r = (num >> 16) & 0xFF;
  let g = (num >> 8) & 0xFF;
  let b = num & 0xFF;
  r = Math.max(0, Math.min(255, Math.round(r * (100 - percent) / 100)));
  g = Math.max(0, Math.min(255, Math.round(g * (100 - percent) / 100)));
  b = Math.max(0, Math.min(255, Math.round(b * (100 - percent) / 100)));
  const rr = r.toString(16).padStart(2,'0');
  const gg = g.toString(16).padStart(2,'0');
  const bb = b.toString(16).padStart(2,'0');
  return `#${rr}${gg}${bb}`;
};

const estadoColores = {
  pedido: "#EE82EE",
  comprado: "#3b82f6",
  "en reparto": "#f59e0b",
  entregado: "#10b981",
  "en la puerta": "#d12b39ff",
};

const SeguimientoDelDia = () => {
  const navigate = useNavigate();
  const [compras, setCompras] = useState([]);
  // Copia completa de compras para derivar rutas (no se reduce al filtrar la vista)
  const [allCompras, setAllCompras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroAbreviatura, setFiltroAbreviatura] = useState("");
  const [filtroRuta, setFiltroRuta] = useState("");
  const { rutas, loading: loadingRutas } = useRutas();
  const { user } = useAuth();
  const usuario = user?.user || user;

  useEffect(() => {
    if (!usuario) return;
    // Al montar o cambiar usuario, traemos la copia completa para poblar las rutas
    fetchAllCompras();
    // Y traemos la vista filtrada actual (si hay filtros)
    fetchCompras();
  }, [usuario]);

  useEffect(() => {
    if (!usuario) return;
    // Cuando cambian los filtros, refrescamos la vista (pero no la copia completa)
    fetchCompras();
  }, [usuario, filtroAbreviatura, filtroRuta]);

  const fetchAllCompras = async () => {
    try {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const hoy = `${yyyy}-${mm}-${dd}`;
      // Normalizar filtroAbreviatura:
      //  - eliminar todos los espacios (leading, trailing e internos)
      //  - conservar puntos SOLO si están entre dos dígitos (p.ej. versiones o decimales)
      //  - convertir a minúsculas
      const normalizeAbreviatura = (s) => {
        if (!s) return '';
        // eliminar espacios
        const noSpaces = String(s).replace(/\s+/g, '');
        const lower = noSpaces.toLowerCase();
        // conservar solo los puntos que estén entre dígitos
        let out = '';
        for (let i = 0; i < lower.length; i++) {
          const ch = lower[i];
          if (ch === '.') {
            const prev = lower[i - 1];
            const next = lower[i + 1];
            if (prev && next && /\d/.test(prev) && /\d/.test(next)) {
              out += '.'; // keep decimal/version dot
            } else {
              // drop the dot
            };
          }
        }
        return out;
      };
      const res = await axios.get(
        `/api/seguimientoDia/compras?user_supervisora=${encodeURIComponent(usuario)}&hoy=${hoy}`
      );
      setAllCompras(res.data.items || []);
    } catch (err) {
      // No bloquear la vista si falla esta llamada; el fetch normal lo notificará
      console.warn('No se pudo cargar allCompras', err);
    }
  };

  const fetchCompras = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const hoy = `${yyyy}-${mm}-${dd}`;
      // Normalizar filtroAbreviatura:
      //  - eliminar todos los espacios (leading, trailing e internos)
      //  - conservar puntos SOLO si están entre dos dígitos (p.ej. versiones o decimales)
      //  - convertir a minúsculas
      const normalizeAbreviatura = (s) => {
        if (!s) return '';
        // eliminar espacios
        const noSpaces = String(s).replace(/\s+/g, '');
        const lower = noSpaces.toLowerCase();
        // conservar solo los puntos que estén entre dígitos
        let out = '';
        for (let i = 0; i < lower.length; i++) {
          const ch = lower[i];
          if (ch === '.') {
            const prev = lower[i - 1];
            const next = lower[i + 1];
            if (prev && next && /\d/.test(prev) && /\d/.test(next)) {
              out += '.'; // keep decimal/version dot
            } else {
              // drop the dot
            }
          } else {
            out += ch;
          }
        }
        return out;
      };
      const filtroAbreviaturaNormalized = normalizeAbreviatura(filtroAbreviatura);
      const res = await axios.get(
        `/api/seguimientoDia/compras?user_supervisora=${encodeURIComponent(usuario)}&hoy=${hoy}&filtroAbreviatura=${encodeURIComponent(filtroAbreviaturaNormalized)}&filtroRuta=${encodeURIComponent(filtroRuta)}`
      );
      setCompras(res.data.items || []);
      setLoading(false);
    } catch (err) {
      setError("❌ Error al cargar las compras");
      setLoading(false);
    }
  };

  // Agrupar por estado
  const comprasSafe = Array.isArray(compras) ? compras : [];
  const total = comprasSafe.length;
  const estados = Object.keys(estadoColores);
  const resumenEstados = estados.map(estado => ({
    estado,
    count: comprasSafe.filter(c => c.estado === estado).length,
    color: estadoColores[estado]
  })).filter(e => e.count > 0);

  // Colores de rutas (igual que PisoCrearCompras)
  // Prefer derivar rutas desde los datos de `compras` (como hace PisoCrearCompras con pisos)
  // para asegurarnos de que el select siempre se pueble; usar `rutas` (facetas) como fallback.
  // Preferir la lista completa (allCompras) para derivar las rutas, así el select
  // no se reduce cuando el usuario aplica un filtro a la vista.
  const rutasFromCompras = Array.isArray(allCompras) && allCompras.length > 0
    ? allCompras.map(c => c.ruta ?? c.ruta_piso)
    : (Array.isArray(comprasSafe) ? comprasSafe.map(c => c.ruta ?? c.ruta_piso) : []);
  const rutasFuente = rutasFromCompras.length > 0 ? rutasFromCompras : (Array.isArray(rutas) ? rutas : []);
  const rutasUnicas = Array.from(
    new Set(
      rutasFuente
        .map(r => {
          if (r == null) return '';
          if (typeof r === 'object') return String(r.ruta ?? r.value ?? r.id ?? '');
          return String(r);
        })
        .filter(s => s !== '')
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const coloresRutas = [
    "#4ECDC4", // verde azulado
    "#4CAF50",
    "#556270",
    "#C7F464",
    "#C44DFF",
    "#FFB347"
  ];
  const obtenerColorRuta = (ruta) => {
    if (ruta === null || ruta === undefined || ruta === '') return '#888';
    const rutaStr = String(ruta);
    const index = rutasUnicas.findIndex(x => String(x) === rutaStr);
    if (index === -1) return '#888';
    return coloresRutas[index % coloresRutas.length] || '#888';
  };

  // Estilo compartido para filtros (input y select) para que tengan el mismo aspecto/ancho
  const filterStyle = {
    padding: "8px 12px",
    fontSize: 16,
    height: 40,
    border: "1px solid #ccc",
    borderRadius: 4,
    minWidth: 160,
    width: 180,
    boxSizing: "border-box",
    lineHeight: "20px",
    backgroundColor: "#fff",
    color: "#111",
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 24, fontFamily: "Arial, sans-serif" }}>
      <style>{`
        details summary::-webkit-details-marker { display: none; }
        details summary::marker { display: none; }
        details summary { position: relative; padding-left: 20px; }
        details summary::before {
          content: '▶';
          position: absolute;
          left: 0;
          transition: transform 0.2s;
          font-size: 11px;
        }
        details[open] summary::before {
          transform: rotate(90deg);
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
        <button
          onClick={() => navigate('/supervisora/compras')}
          style={{ padding: "7px 16px", borderRadius: 16, background: "linear-gradient(135deg,#3b82f6,#1d4ed8)", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, boxShadow: "0 2px 6px rgba(29,78,216,0.3)" }}
        >
          ← Volver
        </button>
        <h2 style={{ flex: 1, textAlign: "center", fontSize: 26, margin: 0, color: "#333" }}>
          Seguimiento del día
        </h2>
      </div>
      {/* Filtros */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap", alignItems: "center", justifyContent: "center" }}>
        <input
          type="text"
          placeholder="Filtrar por abreviatura"
          value={filtroAbreviatura}
          onChange={e => setFiltroAbreviatura(e.target.value)}
          style={filterStyle}
        />

        {/* Select envuelto para poder dibujar una flecha personalizada y tener el mismo ancho */}
        <div style={{ position: 'relative', width: filterStyle.width }}>
          <select
            value={filtroRuta}
            onChange={e => setFiltroRuta(e.target.value)}
            style={{
              ...filterStyle,
              paddingRight: 36,
              paddingLeft: 12,
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              background: filtroRuta ? obtenerColorRuta(filtroRuta) : filterStyle.backgroundColor,
              color: filtroRuta ? getContrastColor(obtenerColorRuta(filtroRuta)) : filterStyle.color,
              border: filtroRuta ? `1px solid ${darkenColor(obtenerColorRuta(filtroRuta), 25)}` : filterStyle.border,
            }}
          >
            <option value="">Filtrar por ruta</option>
            {loadingRutas && <option disabled>Cargando rutas...</option>}
            {rutasUnicas.map(r => (
              <option key={r} value={r} style={{ backgroundColor: obtenerColorRuta(r), color: getContrastColor(obtenerColorRuta(r)) }}>Ruta {r}</option>
            ))}
          </select>
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 14, color: filtroRuta ? '#fff' : '#666' }}>▾</span>
        </div>
      </div>
      {/* Resumen */}
      <div style={{ marginBottom: 18, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span style={{ color:'#1976d2', background:'#e3f2fd', borderRadius:10, padding:'6px 22px', border:'1.5px solid #90caf9', fontSize: 18, fontWeight: 800, letterSpacing:'1px', marginBottom: 6, textTransform:'uppercase', boxShadow:'0 1px 6px #1976d211' }}>
          TOTAL: {total}
        </span>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center', fontSize: 14, fontWeight: 600 }}>
          {resumenEstados.map(({estado, count, color}) => (
            <span
              key={estado}
              style={{
                color,
                border: `1.5px solid ${color}33`,   // borde un poco más marcado
                background: '#fff',
                borderRadius: 10,                   // más redondeado
                padding: '8px 14px',                // más alto y ancho
                fontWeight: 900,                    // más gordo
                fontSize: 15,                       // un poco más grande
                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                lineHeight: 1,
                display: 'inline-block',
              }}
            >
              {estado.charAt(0).toUpperCase() + estado.slice(1)}: {count}
            </span>
          ))}
        </div>
      </div>
      {/* Lista de compras */}
      {loading ? (
        <p>Cargando compras...</p>
      ) : error ? (
        <p>{error}</p>
      ) : comprasSafe.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: 30, fontSize: 18, color: "#666" }}>No hay compras para mostrar.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {comprasSafe.map((compra) => {
            const tieneCambios = Array.isArray(compra.cambios) && compra.cambios.length > 0;
            return (
              <div key={compra.id} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1.5px solid #b3c6e0',
                    borderRadius: 12,
                    padding: '14px 20px',
                    background: 'linear-gradient(90deg, #f7fbff 60%, #e3f2fd 100%)',
                    fontSize: 16,
                    boxShadow: '0 4px 16px 0 rgba(25,118,210,0.07)',
                    transition: 'box-shadow 0.2s',
                    gap: 0,
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0,
                    minWidth: 0,
                    maxWidth: '70%',
                    flexShrink: 1,
                    overflow: 'hidden',
                  }}>
                    <span style={{
                      fontWeight: 800,
                      fontSize: 17,
                      color: '#1976d2',
                      border: '2px solid #1976d2',
                      borderRadius: 8,
                      padding: '4px 14px',
                      fontFamily: "'Montserrat', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                      letterSpacing: '1.1px',
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase',
                      marginRight: 10,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 160,
                      display: 'inline-block',
                    }}>{compra.abreviatura_piso.split("(")[0]}</span>
                    <span style={{
                      fontSize: 13,
                      fontWeight: 700,
                      marginRight: 10,
                      background: obtenerColorRuta(compra.ruta),
                      color: '#fff',
                      borderRadius: 8,
                      padding: '4px 4px',
                      minWidth: 30,
                      maxWidth: 80,
                      textAlign: 'center',
                      boxShadow: '0 2px 6px #0001',
                      letterSpacing: '0.5px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                    }}>
                      R{compra.ruta ? compra.ruta : '—'}
                    </span>
                  </div>
                  <span style={{ fontWeight: 750, color: estadoColores[compra.estado] || "#555", border: `2px solid ${estadoColores[compra.estado] || "#555"}`, padding: '3px 14px', borderRadius: 14, fontSize: 14, backgroundColor: '#f3f3f3', marginRight: 0 }}>{compra.estado}</span>
                </div>

                {/* Mostrar cambios si existen */}
                {tieneCambios && (
                  <details style={{
                    marginTop: 6,
                    marginLeft: 12,
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
                    border: '1.5px solid #ffc107',
                    borderRadius: 10,
                    boxShadow: '0 2px 8px rgba(255,193,7,0.15)',
                  }}>
                    <summary style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#856404',
                      marginBottom: 0,
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      userSelect: 'none',
                      listStyle: 'none',
                      paddingLeft: 20,
                    }}>
                      <span style={{ fontSize: 16 }}>⚠️</span>
                      <span>Cambios realizados ({compra.cambios.length})</span>
                    </summary>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                      {compra.cambios.map((cambio, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          padding: '8px 12px',
                          background: '#fff',
                          borderRadius: 8,
                          border: '1px solid #e0e0e0',
                          fontSize: 12,
                        }}>
                          {/* Producto eliminado */}
                          {cambio.id_producto_eliminado && cambio.id_producto_eliminado !== 1 && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              color: '#d32f2f',
                            }}>
                              <span style={{ fontSize: 14, fontWeight: 700 }}>❌</span>
                              <span style={{ textDecoration: 'line-through', flex: 1 }}>
                                {cambio.nombre_eliminado}
                              </span>
                              <span style={{ fontWeight: 600 }}>
                                {cambio.cantidad_eliminado} ud.
                              </span>
                              <span style={{ fontWeight: 700 }}>
                                {Number(cambio.precio_eliminado || 0).toFixed(2)} €
                              </span>
                            </div>
                          )}
                          {/* Producto añadido */}
                          {cambio.id_producto_añadido && cambio.id_producto_añadido !== 1 && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              color: '#2e7d32',
                            }}>
                              <span style={{ fontSize: 14, fontWeight: 700 }}>✅</span>
                              <span style={{ flex: 1, fontWeight: 600 }}>
                                {cambio.nombre_añadido}
                              </span>
                              <span style={{ fontWeight: 600 }}>
                                {cambio.unidades_añadido} ud.
                              </span>
                              <span style={{ fontWeight: 700 }}>
                                {Number(cambio.precio_añadido || 0).toFixed(2)} €
                              </span>
                            </div>
                          )}
                          {/* Caso especial: no sustitución (solo eliminado) */}
                          {cambio.id_producto_añadido === 1 && (
                            <div style={{
                              fontSize: 11,
                              fontStyle: 'italic',
                              color: '#666',
                              marginTop: 2,
                            }}>
                              (No sustituido)
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SeguimientoDelDia;
